import d3 = require("d3");
import React = require("react");
import ReactFauxDOM = require("react-faux-dom");
import {MarketingTabMapper} from "../../locations/marketing/utils/MarketingTabMapper";
import "../../locations/marketing/charts/Chart.scss";

export interface PieChartRow {
    label: string;
    frequency: number;
    color: string;
}

export interface PieChartProps {
    radius: number;
    data: PieChartRow[];
    selectedItem: string;
    onPathHover: (...d: any[]) => void;
}

export class PieChart extends React.Component<PieChartProps, {}> {

    private static DEFAULT_COLOR: string = "var(--vebox-gray-hover-light)";

    constructor(props: PieChartProps) {
        super(props);
        this.mouseOut = this.mouseOut.bind(this);
        this.mouseOver = this.mouseOver.bind(this);
    }

    public render() {
        const div = new ReactFauxDOM.Element("div");
        const data: PieChartRow[] = this.props.data;
        const radius = this.props.radius;
        const pieDimensions = {
            radius: this.props.radius,
            width: this.props.radius * 2,
        };
        pieDimensions.radius = Math.min(pieDimensions.width, pieDimensions.width) / 2;
        const svg = d3.select(div).append("svg")
            .attr("width", pieDimensions.width + 50).attr("height", pieDimensions.width + 50);
        const pieSvg = svg.append("g")
            .attr(
                "transform",
                "translate(" + (pieDimensions.width + 50) / 2 + "," + (pieDimensions.width + 50) / 2 + ")",
            );
        const filterId = "dropshadow" + Math.random() * 100000;
        const filter = pieSvg.append("filter")
            .attr("id", filterId);

        filter.append("feGaussianBlur")
            .attr("in", "SourceAlpha")
            .attr("stdDeviation", 4)
            .attr("result", "blur");
        filter.append("feOffset")
            .attr("in", "blur")
            .attr("dx", 2)
            .attr("dy", 2)
            .attr("result", "offsetBlur");
        filter.append("feFlood")
            .attr("in", "offsetBlur")
            .attr("flood-color", "#3d3d3d")
            .attr("flood-opacity", "0.5")
            .attr("result", "offsetColor");
        filter.append("feComposite")
            .attr("in", "offsetColor")
            .attr("in2", "offsetBlur")
            .attr("operator", "in")
            .attr("result", "offsetBlur");

        pieSvg.style("filter", "url(#" + filterId + ")");

        const feMerge = filter.append("feMerge");

        feMerge.append("feMergeNode")
            .attr("in", "offsetBlur");
        feMerge.append("feMergeNode")
            .attr("in", "SourceGraphic");

        const outerRadius = pieDimensions.radius - 10;
        const innerRadius = outerRadius / 4;
        const arc = d3.arc().outerRadius(pieDimensions.radius - 10).innerRadius(innerRadius);
        const pie = d3.pie().value((d) => (d as any).frequency);
        pieSvg.selectAll("path")
            .data(pie(data as any))
            .enter()
            .append("path")
            .style("opacity", ".8")
            .attr("data-id", (d) => {
                return MarketingTabMapper.codifyEntityName((d as any).data.label);
            })
            .attr("d", arc as any)
            .style("fill", (d) => {
                return (d as any).data.color;
            })
            .on("mouseover", this.mouseOver)
            .on("mouseout", this.mouseOut);

        function midAngle(d) {
            return d.startAngle + (d.endAngle - d.startAngle) / 2;
        }

        const dataSet = this.props.data;
        const total = dataSet.map((item) => {
            return item.frequency;
        }).reduce((a, b) => {
            return a + b;
        }, 0);
        const getPercentage = (t: number, value: number): number => {
            return 100 * value / t;
        };
        pieSvg.append("g")
            .attr("class", "pie-chart__labels");
        pieSvg.append("g")
            .attr("class", "lines");
        const text = svg.select(".pie-chart__labels").selectAll("text")
            .data(pie(data as any));
        text.enter()
            .append("text")
            .attr("dy", ".35em")
            .text((d) => {
                const percentage = getPercentage(total, (d as any).data.frequency);
                let value = percentage.toFixed(1) + "%";
                if (percentage < 0.1 && percentage > 0) {
                    value = "< 0.1%";
                }
                return value;
            })
            .attr("transform", (d) => {
                const percentage = getPercentage(total, (d as any).data.frequency);
                const pos = arc.centroid(d as any);
                if (percentage > 5) {
                    return "translate(" + pos + ")";
                }
                return "translate(" + [radius * Math.sin(midAngle(d)), -(radius + 5) * Math.cos(midAngle(d))] + ")";
            })
            .attr("text-anchor", "middle")
            .attr("class", (d) => {
                const percentage = getPercentage(total, (d as any).data.frequency);
                if (percentage < 5) {
                    return "labels--hidden labels--black";
                }
                return "";
            })
            .attr("data-id", (d: any) => {
                return "labels" + MarketingTabMapper.codifyEntityName(d.data.label);
            });
        if (this.props.selectedItem != null) {
            pieSvg.selectAll("[data-id=" + MarketingTabMapper.codifyEntityName(this.props.selectedItem) + "]")
                .style("fill", PieChart.DEFAULT_COLOR);
            pieSvg.selectAll("[data-id=labels" + MarketingTabMapper.codifyEntityName(this.props.selectedItem) + "]")
                .classed("labels--visible", true)
                .classed("labels--hidden", false);
        }
        return div.toReact();
    }

    public mouseOver(event, d) {
        this.props.onPathHover(d.data.label);
    }

    public mouseOut() {
        this.props.onPathHover(null);
    }
}
